import { useState } from 'react';
import { api } from '@/lib/api';

const EXPERTISE_AREAS = ['AI 研究', 'ML 工程', '產品管理', '資料科學', '策略顧問', '軟體工程', '職涯規劃', '創業經驗'];

const MentorForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    company: '',
    linkedin: '',
    expertise: '',
    experience: '',
    intro: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.role || !formData.company || !formData.expertise) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.submitWebsiteContact({
        formType: 2,
        companyName: formData.company,
        contactName: formData.name,
        email: formData.email,
        currentRole: formData.role,
        linkedinUrl: formData.linkedin,
        expertiseArea: formData.expertise,
        experienceYears: formData.experience,
        message: formData.intro
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '提交失敗，請稍後重試');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-violet-100 rounded-full mb-5">
          <i className="ri-user-star-line text-violet-600 text-2xl" />
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-2">申請已收到！</h3>
        <p className="text-slate-500 text-sm leading-relaxed max-w-xs">
          我們會在 <strong>3-5 個工作日</strong>內審核你的申請，
          通過後將透過電子郵件傳送下一步引導。
        </p>
        <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
          <i className="ri-mail-check-line text-violet-400" />
          確認郵件已傳送至 {formData.email}
        </div>
        <button
          onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', role: '', company: '', linkedin: '', expertise: '', experience: '', intro: '' }); }}
          className="mt-4 px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-violet-700 transition-colors whitespace-nowrap"
        >
          重新填寫
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-5"
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            真實姓名 <span className="text-rose-500">*</span>
          </label>
          <input
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="你的全名（中英文均可）"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            聯絡信箱 <span className="text-rose-500">*</span>
          </label>
          <input
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="you@company.com"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            現任職位 <span className="text-rose-500">*</span>
          </label>
          <input
            name="role"
            type="text"
            required
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            placeholder="例：Senior ML Engineer"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            所在公司 <span className="text-rose-500">*</span>
          </label>
          <input
            name="company"
            type="text"
            required
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            placeholder="例：NVIDIA / Google"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">LinkedIn 主页</label>
          <input
            name="linkedin"
            type="url"
            value={formData.linkedin}
            onChange={(e) => handleChange('linkedin', e.target.value)}
            placeholder="https://linkedin.com/in/..."
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">從業年限</label>
          <select
            name="experience"
            value={formData.experience}
            onChange={(e) => handleChange('experience', e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-violet-400 focus:bg-white transition-all cursor-pointer"
          >
            <option value="">請選擇</option>
            <option value="3-5年">3–5 年</option>
            <option value="5-8年">5–8 年</option>
            <option value="8-12年">8–12 年</option>
            <option value="12年以上">12 年以上</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">
          專業領域 <span className="text-rose-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {EXPERTISE_AREAS.map((area) => (
            <button
              key={area}
              type="button"
              onClick={() => handleChange('expertise', area)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                formData.expertise === area
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-violet-50 hover:text-violet-700'
              }`}
            >
              {area}
            </button>
          ))}
        </div>
        <input type="hidden" name="expertise" value={formData.expertise} />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">個人簡介 & 輔導風格</label>
        <textarea
          name="intro"
          value={formData.intro}
          onChange={(e) => {
            if (e.target.value.length <= 500) handleChange('intro', e.target.value);
          }}
          placeholder="簡單介紹你的背景、擅長幫助的族群，以及你希望透過 NexusGo 做什麼..."
          rows={4}
          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all resize-none"
        />
        <p className="text-xs text-slate-400 mt-1 text-right">{formData.intro.length}/500</p>
      </div>

      <button
        type="submit"
        disabled={submitting || !formData.name || !formData.email || !formData.role || !formData.company || !formData.expertise}
        className="w-full py-3.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap disabled:cursor-not-allowed"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin" />
            提交中...
          </span>
        ) : '提交導師申請'}
      </button>
      {submitError ? <p className="text-center text-xs text-rose-500">{submitError}</p> : null}
    </form>
  );
};

export default MentorForm;
